import { useEffect, useState } from 'react'
import _ from 'lodash'
import { Card, Accordion, Grid, Input } from '@design-systems/canvas-ui'

/**
 * Generic reaction grid
 * - multiple "Segment(s)" each inside an accordian/expander
 * - each "Segment" having multiple "Metrice(s)"
 * - each "Metric" with options to choose from in columns
 * @param {*} props
 * @returns
 */

const buildCompleteValueMatrix = (template, currentValue) => {
  const existingSegments = currentValue?.segments || []

  return {
    ...(currentValue || {}),
    segments: (template?.segments || []).map((segment) => {
      const existingSegment = existingSegments.find(
        (item) => item.title === segment.header,
      )

      const existingValues = existingSegment?.values || {}

      return {
        title: segment.header,
        values: (segment.matrices || []).reduce((acc, metric) => {
          acc[metric] = existingValues[metric] ?? ''
          return acc
        }, {}),
      }
    }),
  }
}

const PackedReactionGrid = (props) => {
  const [valueMatrix, setValueMatrix] = useState(() =>
    buildCompleteValueMatrix(props.template, props.value),
  )

  useEffect(() => {
    if (!props.template?.segments?.length) {
      return
    }

    const completeValueMatrix = buildCompleteValueMatrix(
      props.template,
      props.value,
    )

    setValueMatrix((previousValueMatrix) =>
      _.isEqual(previousValueMatrix, completeValueMatrix)
        ? previousValueMatrix
        : completeValueMatrix,
    )
  }, [props.template, props.value])
  useEffect(() => {
    if (!_.isEqual(valueMatrix, props.value) && !props.isReadOnly) {
      props.onGridUpdate(valueMatrix)
    }
  }, [valueMatrix]) // eslint-disable-line react-hooks/exhaustive-deps

  //#endregion

  const getSegmentErrors = (segmentIndex) => {
    const controls = props.errorCache?.controls || {}

    const segmentErrorPrefix = `${props.controlPath}.segments[${segmentIndex}].values`

    return Object.entries(controls).filter(([errorPath]) =>
      errorPath.startsWith(segmentErrorPrefix),
    )
  }

  /**
   * Get the radio buttons state for any participating radios
   * @param {*} selectedSegment
   * @param {*} selectedMetric
   * @returns
   */
  const getRadioButtonState = (selectedSegment, selectedMetric) => {
    const foundSegment = valueMatrix?.segments?.find(
      (segment) => segment.title === selectedSegment.header,
    )
    return foundSegment?.values?.[selectedMetric] || ''
  }
  /**
   * Update any radio button state
   * @param {*} selectedSegment
   * @param {*} selectedMetric
   * @param {*} newValue
   */
  const updateGridMatrix = (selectedSegment, selectedMetric, newValue) => {
    let foundSegmentIndex = valueMatrix?.segments?.findIndex(
      (eachSegment) => eachSegment.title === selectedSegment.header,
    )

    if (foundSegmentIndex >= 0) {
      //a. Segment found -> update existing segment
      let newMetrices = {
        ...valueMatrix.segments[foundSegmentIndex].values,
        [selectedMetric]: newValue,
      }
      setValueMatrix({
        ...valueMatrix,
        segments: [
          ...valueMatrix.segments.map((eachSegment) =>
            eachSegment.title !== selectedSegment.header
              ? eachSegment
              : { ...eachSegment, values: newMetrices },
          ),
        ],
      })
    } else {
      //b. New segment -> add new segment + add new metric
      let newSegments = [
        ...valueMatrix.segments,
        {
          title: selectedSegment.header,
          values: { [selectedMetric]: newValue },
        },
      ]
      setValueMatrix({ ...valueMatrix, segments: newSegments })
    }
  }

  return (
    <div>
      <Input.Label className="hc8-fs-100 hc8-pv-3x">
        <strong>{props.label}</strong>
      </Input.Label>
      <Card id="card-packed-rg" variant="elevated">
        <table
          name={`Packed input grid for ${props.template.title}`}
          id={`table-${_.camelCase(props.template.title)}`}
          className="packed-reaction-grid__table"
          style={{ width: '100%', borderCollapse: 'collapse' }}
        >
          <colgroup>
            <col className="packed-reaction-grid__capability-column" />
            {_.times(props.template.value_options.length, (index) => (
              <col key={`table-column-${index}`} />
            ))}
          </colgroup>
          <thead className="hc8-bg-subdued border-bottom-grey-thin">
            {/* Header - Overall */}
            <tr className="hc8-pv-2x">
              <th
                key={`tbl-header-0`}
                style={{ width: '41.66%', textAlign: 'left' }}
              >
                <label className="hc8-fs-050 hc8-pl-10x hc8-ml-2x">
                  <strong>{props.template.title}</strong>
                </label>
              </th>
              {props.template.value_options.map(
                (eachValueOption, headerIndex) => (
                  <th
                    key={`tbl-header-${headerIndex + 1}`}
                    style={{
                      textAlign: 'center',
                      borderLeft:
                        '1px solid var(--canvas-border-color--default)',
                    }}
                  >
                    <label className="hc8-fs-050">
                      <strong>{eachValueOption}</strong>
                    </label>
                  </th>
                ),
              )}
            </tr>
          </thead>

          <tbody>
            {/* 1. Segments */}
            {props.template.segments.map((eachSegment, segmentIndex) => {
              const segmentErrors = getSegmentErrors(segmentIndex)
              const hasSegmentError = segmentErrors.length > 0
              return (
                <tr
                  key={`row-segment-${segmentIndex}`}
                  id={`row-segment-${segmentIndex}`}
                >
                  <td colSpan={props.template.value_options.length + 1}>
                    <Accordion
                      chevronLocation="left"
                      spacing="none"
                      startOpen={segmentIndex === 0}
                      className={`packed-reaction-grid__accordion width-100 border-bottom-grey-thin ${
                        hasSegmentError
                          ? 'strength-assessment-segment-error'
                          : ''
                      }`}
                      header={
                        <div>
                          <div
                            className={`hc8-fs-050 ${
                              hasSegmentError
                                ? 'strength-assessment-segment-heading error-color'
                                : 'strength-assessment-segment-heading'
                            }`}
                          >
                            {eachSegment.header}
                          </div>

                          {hasSegmentError && (
                            <div className="strength-assessment-segment-validation error-color">
                              Please answer all values in this section.
                            </div>
                          )}
                        </div>
                      }
                    >
                      <div id={`exp-content-${segmentIndex}`}>
                        {/* 1aa. Each segment reaction grid */}
                        <table
                          name={`Segment for ${eachSegment.header}`}
                          key={`table-segment-${segmentIndex}`}
                          className="packed-reaction-grid__table"
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                          }}
                        >
                          <colgroup>
                            <col className="packed-reaction-grid__capability-column" />
                            {_.times(
                              props.template.value_options.length,
                              (index) => (
                                <col key={`segment-column-${index}`} />
                              ),
                            )}
                          </colgroup>
                          <tbody>
                            {eachSegment.matrices.map(
                              (eachMetric, metricIndex) => {
                                const radioName = `radio-${props.rowIndex}-${segmentIndex}-${metricIndex}`
                                const selectedValue = getRadioButtonState(
                                  eachSegment,
                                  eachMetric,
                                )
                                return (
                                  <tr
                                    key={`table-row-segment${segmentIndex}-${metricIndex}`}
                                    className="packed-reaction-grid__metric-row border-top-grey-thin"
                                  >
                                    <td
                                      key={`table-data-segment${segmentIndex}-${metricIndex}-0`}
                                      style={{ width: '41.66%' }}
                                      className="hc8-pv-2x"
                                    >
                                      <label className="hc8-pl-14x hc8-fs-050">
                                        {eachMetric}
                                      </label>
                                    </td>
                                    {_.times(
                                      props.template.value_options.length,
                                      (radioIndex) => {
                                        const optionValue =
                                          props.template.value_options[
                                            radioIndex
                                          ]
                                        const inputId = `radio-${props.rowIndex}-${segmentIndex}-${metricIndex}-${radioIndex}`
                                        return (
                                          <td
                                            key={inputId}
                                            className="border-left-grey-thin hc8-pv-1x"
                                            style={{ textAlign: 'center' }}
                                          >
                                            <Grid.Container
                                              justify="center"
                                              spacing="none"
                                            >
                                              <Grid.Item>
                                                <Input.Radio
                                                  id={inputId}
                                                  data-testid={`radio-${segmentIndex}-${metricIndex}-${radioIndex}`}
                                                  name={radioName}
                                                  value={optionValue}
                                                  isChecked={
                                                    selectedValue ===
                                                    optionValue
                                                  }
                                                  isDisabled={props.isReadOnly}
                                                  aria-label={`${eachMetric}: ${optionValue}`}
                                                  onChange={(event) =>
                                                    updateGridMatrix(
                                                      eachSegment,
                                                      eachMetric,
                                                      event.target.value,
                                                    )
                                                  }
                                                />
                                              </Grid.Item>
                                            </Grid.Container>
                                          </td>
                                        )
                                      },
                                    )}
                                  </tr>
                                )
                              },
                            )}
                          </tbody>
                        </table>
                      </div>
                    </Accordion>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

export default PackedReactionGrid
